import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { invalidateArticleCache } from "@/lib/useAllArticles";
import {
    PenTool, Sparkles, ShieldCheck, Link2, ListChecks, ExternalLink,
    Loader2, AlertTriangle, CheckCircle2, XCircle, Save, Trash2,
    BookOpen, ArrowRight, Plus, Minus, RefreshCcw, FileEdit, Eraser,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Content Studio — paste → extract → review → publish flow.
 *
 * The full studio is intentionally one panel of well-scoped sub-cards
 * rather than a multi-step wizard. Ashlee told us she wants to see
 * everything at once and tweak inline.
 *
 * Steps the UI surfaces (top-to-bottom):
 *   1. PASTE — raw text from BabyLoveGrowth (or anywhere)
 *   2. EXTRACT — LLM turns it into the Memory Lane shape
 *   3. EDIT — every extracted field is editable
 *   4. AI REVIEW — clinical/medical/SEO checklist
 *   5. RELATED — internal cross-links from the existing 94-article corpus
 *   6. PUBLISH — to MongoDB + sitemap (under Ashlee's authorship)
 */
export default function ContentStudio() {
    const [raw, setRaw] = useState("");
    const [article, setArticle] = useState(null);
    const [extracting, setExtracting] = useState(false);
    const [validation, setValidation] = useState(null);
    const [validating, setValidating] = useState(false);
    const [related, setRelated] = useState(null);
    const [relatedLoading, setRelatedLoading] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [draftId, setDraftId] = useState(null);
    // When ?edit_id=<uuid> is on the URL we're updating a live article.
    // The Article Library deep-links here for the Edit action.
    const [editId, setEditId] = useState(() => {
        if (typeof window === "undefined") return null;
        return new URLSearchParams(window.location.search).get("edit_id");
    });
    const [updating, setUpdating] = useState(false);

    useEffect(() => { refreshDrafts(); }, []);
    // Load the live article when ?edit_id changes (deep link from Article Library).
    useEffect(() => {
        if (!editId) return;
        api.get(`/admin/content-studio/articles/${editId}`)
            .then((r) => { setArticle(r.data); setRaw(""); setValidation(null); setLastChanges(null); })
            .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load article."));
    }, [editId]);
    // ----- Update an already-published article -----
    const onUpdatePublished = async () => {
        if (!article || !editId) return;
        setUpdating(true);
        try {
            const r = await api.patch(`/admin/content-studio/articles/${editId}`, {
                title: article.title, h1: article.h1, meta: article.meta, summary: article.summary,
                body: article.body, faq: article.faq, references: article.references, tags: article.tags,
                cta: article.cta, lane_prompt: article.lane_prompt, memory_lane_tip: article.memory_lane_tip,
                category: article.category, stage: article.stage, slug: article.slug, group: article.group,
            });
            setArticle(r.data.article);
            toast.success(`Updated. Changes live now.`);
            invalidateArticleCache();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update.");
        } finally { setUpdating(false); }
    };
    const onExitEditMode = () => {
        const p = new URLSearchParams(window.location.search);
        p.delete("edit_id"); p.set("view", "library");
        window.history.pushState({}, "", `${window.location.pathname}?${p.toString()}`);
        window.dispatchEvent(new PopStateEvent("popstate"));
    };

    const refreshDrafts = () => api.get("/admin/content-studio/drafts").then((r) => setDrafts(r.data.drafts || [])).catch(() => { });

    // ----- step 1+2 -----
    const onExtract = async () => {
        if (raw.trim().length < 50) {
            toast.error("Paste at least a few paragraphs.");
            return;
        }
        setExtracting(true);
        setValidation(null);
        setRelated(null);
        setLastChanges(null);
        try {
            const r = await api.post("/admin/content-studio/extract", { raw_text: raw });
            setArticle(r.data.article);
            toast.success("Article structured. Review and edit below.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't extract — paste a longer draft.");
        } finally {
            setExtracting(false);
        }
    };

    // ----- Apply suggested fixes (LLM transforms the article in place) -----
    const [applyingFixes, setApplyingFixes] = useState(false);
    const [lastChanges, setLastChanges] = useState(null);  // before/after summary
    const onApplyFixes = async () => {
        if (!article) return;
        setApplyingFixes(true);
        setLastChanges(null);
        try {
            const r = await api.post("/admin/content-studio/apply-fixes", {
                article,
                suggested_fixes: validation?.suggested_fixes || [],
                validation_checks: validation?.checks || [],
            });
            const revised = r.data.article;
            const changes = r.data.changes || { count: 0, items: [] };
            setArticle(revised);
            setLastChanges(changes);
            // Persist the revised article so a refresh/re-run doesn't lose it.
            try {
                const dr = await api.post("/admin/content-studio/drafts", { article: revised, id: draftId });
                if (dr?.data?.id && !draftId) setDraftId(dr.data.id);
                refreshDrafts();
            } catch { /* draft save is best-effort */ }
            const msg = changes.count > 0
                ? `Applied ${changes.count} change${changes.count === 1 ? "" : "s"}. Draft auto-saved.`
                : "Article reviewed — no material changes were needed.";
            toast.success(msg);
            // Clear the old AI Review so Ashlee knows to re-run against the revised article.
            setValidation(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't auto-fix.");
        } finally {
            setApplyingFixes(false);
        }
    };

    // ----- step 4 -----
    const onValidate = async () => {
        if (!article) return;
        setValidating(true);
        try {
            const r = await api.post("/admin/content-studio/validate", { article });
            setValidation(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't run AI review.");
        } finally { setValidating(false); }
    };

    // ----- step 5 -----
    const onSuggestRelated = async () => {
        if (!article) return;
        setRelatedLoading(true);
        try {
            const r = await api.post("/admin/content-studio/suggest-related", { article });
            setRelated(r.data.related || []);
        } catch (e) {
            toast.error("Couldn't fetch related articles.");
        } finally { setRelatedLoading(false); }
    };

    // ----- step 6 -----
    const onPublish = async () => {
        if (!article) return;
        if (!window.confirm(`Publish "${article.h1}" to /${article.group}/${article.slug}? This makes it live and adds it to the sitemap.`)) return;
        setPublishing(true);
        try {
            const r = await api.post("/admin/content-studio/publish", { article });
            toast.success(`Published: ${r.data.url}`);
            // Drop the in-memory article cache so search, library, and
            // author page pick up the new article on next mount.
            invalidateArticleCache();
            // Reset to a clean slate so Ashlee can paste the next one.
            setRaw("");
            setArticle(null);
            setValidation(null);
            setRelated(null);
            if (draftId) { await api.delete(`/admin/content-studio/drafts/${draftId}`).catch(() => { }); setDraftId(null); }
            refreshDrafts();
            // Open the new article in a new tab so Ashlee can verify.
            window.open(r.data.url, "_blank", "noopener");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Publish failed.");
        } finally { setPublishing(false); }
    };

    // ----- draft persistence -----
    const onSaveDraft = async () => {
        if (!article) return;
        try {
            const r = await api.post("/admin/content-studio/drafts", { article, id: draftId });
            setDraftId(r.data.id);
            toast.success("Draft saved.");
            refreshDrafts();
        } catch { toast.error("Couldn't save draft."); }
    };
    const onLoadDraft = (d) => {
        setArticle(d.article);
        setDraftId(d.id);
        setValidation(null);
        setRelated(null);
        toast.success(`Loaded draft: ${d.h1}`);
    };
    const onDeleteDraft = async (id) => {
        if (!window.confirm("Delete this draft?")) return;
        await api.delete(`/admin/content-studio/drafts/${id}`).catch(() => { });
        if (id === draftId) setDraftId(null);
        refreshDrafts();
    };

    // ----- Library-wide artifact scan + clean (post-publish hygiene) -----
    // Scans every published article for [INTERNAL LINK: …], [CITATION],
    // [SOURCE], TODO, Lorem ipsum, unrendered Markdown. Internal-link
    // markers pointing at real articles are resolved to the article H1.
    // Dead markers are stripped. Persists changes to MongoDB.
    const [scan, setScan] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [cleaning, setCleaning] = useState(false);
    const onScanArtifacts = async () => {
        setScanning(true);
        try {
            const r = await api.get("/admin/content-studio/scan-artifacts");
            setScan(r.data);
            const dirty = r.data?.dirty ?? 0;
            toast[dirty > 0 ? "warning" : "success"](
                dirty > 0
                    ? `${dirty} of ${r.data.scanned} published article${r.data.scanned === 1 ? "" : "s"} contain artifacts.`
                    : `Library is clean — scanned ${r.data.scanned} article${r.data.scanned === 1 ? "" : "s"}.`
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't scan.");
        } finally { setScanning(false); }
    };
    const onCleanArtifacts = async () => {
        if (!window.confirm("Clean every published article? This rewrites them in MongoDB. Idempotent — safe to re-run.")) return;
        setCleaning(true);
        try {
            const r = await api.post("/admin/content-studio/clean-artifacts", { dry_run: false });
            setScan({ scanned: r.data.scanned, dirty: r.data.cleaned, findings: r.data.report });
            toast.success(`Cleaned ${r.data.cleaned} of ${r.data.scanned} article${r.data.scanned === 1 ? "" : "s"}.`);
            invalidateArticleCache();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't clean.");
        } finally { setCleaning(false); }
    };

    return (
        <div className="space-y-6" data-testid="content-studio">
            {/* Header */}
            <header className="ml-card p-5 sm:p-6 flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center">
                        <PenTool size={18} strokeWidth={1.8} />
                    </span>
                    <div>
                        <h2 className="font-heading text-xl text-[#1F2624] leading-tight">Content Studio</h2>
                        <p className="text-sm text-[#4C5551]">Paste → structure → review → publish under Ashlee&apos;s authorship.</p>
                    </div>
                </div>
                {drafts.length > 0 && (
                    <details className="text-sm" data-testid="studio-drafts-menu">
                        <summary className="cursor-pointer text-teal-dark font-semibold">{drafts.length} saved draft{drafts.length === 1 ? "" : "s"}</summary>
                        <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto bg-cream-light rounded-xl p-2 min-w-[260px]">
                            {drafts.map((d) => (
                                <li key={d.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg hover:bg-white">
                                    <button onClick={() => onLoadDraft(d)} className="flex-1 text-left text-[#1F2624] hover:text-teal-dark truncate" data-testid={`studio-load-draft-${d.id}`}>
                                        {d.h1}
                                    </button>
                                    <button onClick={() => onDeleteDraft(d.id)} aria-label="Delete draft" className="text-[#6E7A74] hover:text-coral-dark" data-testid={`studio-delete-draft-${d.id}`}>
                                        <Trash2 size={14} strokeWidth={1.8} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </header>

            {/* Edit-mode banner — shown when ?edit_id= is on the URL. Replaces
                the Paste step (we're updating an existing live article, not
                creating a new one). */}
            {editId && article && (
                <section className="ml-card p-4 sm:p-5 border-2 border-teal-light bg-teal-soft/40 flex items-center justify-between gap-3 flex-wrap" data-testid="studio-edit-banner">
                    <div className="flex items-center gap-3">
                        <span className="w-10 h-10 rounded-2xl bg-teal text-white flex items-center justify-center">
                            <FileEdit size={18} strokeWidth={1.8} />
                        </span>
                        <div>
                            <h3 className="font-heading text-base text-[#1F2624]">Editing live article</h3>
                            <p className="text-xs text-[#4C5551]">
                                <code className="bg-white px-1.5 py-0.5 rounded text-[11px] mr-1">/{article.group}/{article.slug}</code>
                                Updates publish instantly to the same URL. Change the slug to create a redirect.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onExitEditMode} className="ml-btn-secondary !text-xs !px-3 !py-1.5 !min-h-[32px]" data-testid="studio-edit-cancel">
                            Cancel
                        </button>
                        <a
                            href={`/${article.group}/${article.slug}`}
                            target="_blank" rel="noopener noreferrer"
                            className="ml-btn-secondary !text-xs !px-3 !py-1.5 !min-h-[32px]"
                            data-testid="studio-edit-preview"
                        >
                            <ExternalLink size={12} strokeWidth={2} /> Preview
                        </a>
                        <button
                            onClick={onUpdatePublished}
                            disabled={updating}
                            className="ml-btn-primary !text-xs !px-3 !py-1.5 !min-h-[32px]"
                            data-testid="studio-edit-save"
                        >
                            {updating ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} strokeWidth={2} />}
                            Update Published Article
                        </button>
                    </div>
                </section>
            )}

            {/* Library Hygiene — scan + clean publishing artifacts */}
            <section className="ml-card p-4 sm:p-5 border border-cream-dark" data-testid="studio-hygiene">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5">
                        <span className="w-9 h-9 rounded-2xl bg-yellow-soft text-yellow-dark flex items-center justify-center">
                            <Eraser size={16} strokeWidth={1.8} />
                        </span>
                        <div>
                            <h3 className="font-heading text-base text-[#1F2624]">Library hygiene</h3>
                            <p className="text-xs text-[#4C5551]">
                                Scan every published article for <code className="text-[11px]">[INTERNAL LINK:</code>,
                                <code className="text-[11px]"> [CITATION]</code>,
                                <code className="text-[11px]"> [SOURCE]</code>,
                                <code className="text-[11px]"> TODO</code>,
                                Lorem ipsum, or raw Markdown.
                                Cleaning resolves real links and strips the rest.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onScanArtifacts}
                            disabled={scanning}
                            className="ml-btn-secondary !text-xs !px-3 !py-1.5 !min-h-[32px]"
                            data-testid="studio-hygiene-scan"
                        >
                            {scanning ? <><Loader2 className="animate-spin" size={12} /> Scanning…</> : <>Scan library</>}
                        </button>
                        <button
                            onClick={onCleanArtifacts}
                            disabled={cleaning || !scan || scan.dirty === 0}
                            className="ml-btn-primary !text-xs !px-3 !py-1.5 !min-h-[32px] disabled:opacity-50"
                            data-testid="studio-hygiene-clean"
                        >
                            {cleaning ? <><Loader2 className="animate-spin" size={12} /> Cleaning…</> : <><Eraser size={12} strokeWidth={2} /> Clean now</>}
                        </button>
                    </div>
                </div>
                {scan && (
                    <div className="mt-3 text-xs text-[#1F2624]" data-testid="studio-hygiene-result">
                        <p>
                            Scanned <strong>{scan.scanned}</strong> article{scan.scanned === 1 ? "" : "s"} —
                            {" "}
                            <strong className={scan.dirty > 0 ? "text-coral-dark" : "text-stage_early-dark"}>
                                {scan.dirty} {scan.dirty === 1 ? "needs" : "need"} cleanup
                            </strong>
                            .
                        </p>
                        {scan.dirty > 0 && (
                            <ul className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
                                {scan.findings.map((f, i) => (
                                    <li key={i} className="rounded-lg bg-cream-light px-2.5 py-1.5">
                                        <a
                                            href={`/${f.group}/${f.slug}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-teal-dark hover:underline font-medium"
                                            data-testid={`studio-hygiene-finding-${f.slug}`}
                                        >
                                            /{f.group}/{f.slug}
                                        </a>
                                        <span className="text-[#4C5551]"> — {f.h1}</span>
                                        <div className="text-[10px] text-[#6E7A74] mt-0.5">
                                            {Object.entries(f.counts).map(([k, v]) => `${k}=${v}`).join(" · ")}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </section>

            {/* 1. Paste */}
            <section className="ml-card p-5 sm:p-6" data-testid="studio-paste">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark mb-1">Step 1</p>
                <h3 className="font-heading text-lg text-[#1F2624]">Paste the BabyLoveGrowth draft</h3>
                <p className="text-sm text-[#4C5551] mt-1">Paste the entire article. Title, body, FAQ, references — all of it. The LLM will structure it for you.</p>
                <textarea
                    value={raw}
                    onChange={(e) => setRaw(e.target.value)}
                    placeholder="Paste your full article here…"
                    rows={10}
                    data-testid="studio-paste-textarea"
                    className="ml-input mt-3 w-full resize-y !min-h-[200px] font-mono text-[13px]"
                />
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                        onClick={onExtract}
                        disabled={extracting || raw.trim().length < 50}
                        data-testid="studio-extract"
                        className="ml-btn-primary disabled:opacity-60"
                    >
                        {extracting ? <><Loader2 className="animate-spin" size={16} /> Structuring…</> : <><Sparkles size={16} strokeWidth={1.8} /> Extract &amp; structure</>}
                    </button>
                    <span className="text-xs text-[#6E7A74]">{raw.length.toLocaleString()} characters</span>
                </div>
            </section>

            {/* 2. Edit */}
            {article && (
                <section className="ml-card p-5 sm:p-6" data-testid="studio-edit">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark mb-1">Step 2</p>
                    <h3 className="font-heading text-lg text-[#1F2624]">Review &amp; edit the structured article</h3>
                    <p className="text-sm text-[#4C5551]">Every field is editable. Memory Lane will format the published page automatically.</p>

                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <FieldGroup>
                            <Field label="Title (≤ 60)" testid="studio-field-title">
                                <input className="ml-input w-full" value={article.title || ""} onChange={(e) => setArticle({ ...article, title: e.target.value })} />
                            </Field>
                            <Field label="H1" testid="studio-field-h1">
                                <input className="ml-input w-full" value={article.h1 || ""} onChange={(e) => setArticle({ ...article, h1: e.target.value })} />
                            </Field>
                            <Field label="Slug" testid="studio-field-slug">
                                <input className="ml-input w-full font-mono text-[13px]" value={article.slug || ""} onChange={(e) => setArticle({ ...article, slug: e.target.value })} />
                            </Field>
                            <Field label="Group" testid="studio-field-group">
                                <select className="ml-input w-full" value={article.group || "learn"} onChange={(e) => setArticle({ ...article, group: e.target.value })}>
                                    {["learn", "behavior", "guide", "paying-for-care", "resources", "treatments", "hard-conversations"].map((g) => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </Field>
                        </FieldGroup>
                        <FieldGroup>
                            <Field label="Meta description (140-160)" testid="studio-field-meta">
                                <textarea rows={3} className="ml-input w-full resize-y" value={article.meta || ""} onChange={(e) => setArticle({ ...article, meta: e.target.value })} />
                            </Field>
                            <Field label="Summary" testid="studio-field-summary">
                                <textarea rows={2} className="ml-input w-full resize-y" value={article.summary || ""} onChange={(e) => setArticle({ ...article, summary: e.target.value })} />
                            </Field>
                            <Field label="Tags (comma-separated)" testid="studio-field-tags">
                                <input className="ml-input w-full" value={(article.tags || []).join(", ")} onChange={(e) => setArticle({ ...article, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} />
                            </Field>
                            <Field label="Lane prompt (seeds Ask Lane)" testid="studio-field-laneprompt">
                                <input className="ml-input w-full" value={article.lane_prompt || ""} onChange={(e) => setArticle({ ...article, lane_prompt: e.target.value })} />
                            </Field>
                        </FieldGroup>
                    </div>

                    {/* Body editor */}
                    <BodyEditor article={article} onChange={setArticle} />

                    {/* FAQ editor */}
                    <FaqEditor article={article} onChange={setArticle} />

                    {/* References */}
                    <RefEditor article={article} onChange={setArticle} />

                    {/* Memory Lane tip + CTA */}
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <Field label="Memory Lane Tip" testid="studio-field-tip">
                            <textarea rows={2} className="ml-input w-full resize-y" value={article.memory_lane_tip || ""} onChange={(e) => setArticle({ ...article, memory_lane_tip: e.target.value })} />
                        </Field>
                        <Field label="CTA line" testid="studio-field-cta">
                            <input className="ml-input w-full" value={article.cta || ""} onChange={(e) => setArticle({ ...article, cta: e.target.value })} />
                        </Field>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2">
                        <button onClick={onSaveDraft} className="ml-btn-secondary" data-testid="studio-save-draft">
                            <Save size={14} strokeWidth={1.8} /> Save draft
                        </button>
                        <button onClick={onValidate} disabled={validating} className="ml-btn-secondary" data-testid="studio-validate">
                            {validating ? <><Loader2 className="animate-spin" size={14} /> Reviewing…</> : <><ShieldCheck size={14} strokeWidth={1.8} /> Run AI review</>}
                        </button>
                        <button onClick={onSuggestRelated} disabled={relatedLoading} className="ml-btn-secondary" data-testid="studio-suggest-related">
                            {relatedLoading ? <><Loader2 className="animate-spin" size={14} /> Finding…</> : <><Link2 size={14} strokeWidth={1.8} /> Suggest related</>}
                        </button>
                    </div>
                </section>
            )}

            {/* 3. Apply Fixes — before/after summary */}
            {lastChanges && <ChangesReport changes={lastChanges} onDismiss={() => setLastChanges(null)} />}

            {/* 4. AI Review */}
            {validation && <ValidationReport validation={validation} onRerun={onValidate} onApplyFixes={onApplyFixes} applyingFixes={applyingFixes} />}

            {/* 5. Related */}
            {related && <RelatedReport related={related} article={article} />}

            {/* 5. Publish */}
            {article && (
                <section className="ml-card p-5 sm:p-6 bg-gradient-to-br from-teal-soft/30 to-cream-light" data-testid="studio-publish">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark mb-1">Final step</p>
                    <h3 className="font-heading text-lg text-[#1F2624]">Approve &amp; publish</h3>
                    <p className="text-sm text-[#4C5551] mt-1">
                        Publishing will: persist the article to MongoDB, append the URL to <code className="text-[12px]">sitemap.xml</code>,
                        and make it live at <code className="text-[12px]">/{article.group}/{article.slug}</code> — with Article + FAQ + Breadcrumb schema,
                        author byline (Ashlee Skabla Velez, APRN, ACNPC-AG), and an author box.
                    </p>
                    <ul className="mt-3 text-xs text-[#4C5551] space-y-1">
                        <li>· Author: <strong>Ashlee Skabla Velez, APRN, ACNPC-AG</strong> (Person schema → /authors/ashlee-skabla-velez)</li>
                        <li>· Publisher: <strong>Memory Lane Care</strong> (Organization)</li>
                        <li>· Indexes added: Article, FAQPage ({(article.faq || []).length} Q/A), BreadcrumbList</li>
                        <li>· Search: live across Memory Lane&apos;s universal search instantly</li>
                    </ul>
                    <button
                        onClick={onPublish}
                        disabled={publishing}
                        data-testid="studio-publish-button"
                        className="ml-btn-primary mt-5 !min-h-[48px]"
                    >
                        {publishing ? <><Loader2 className="animate-spin" size={16} /> Publishing…</> : <><CheckCircle2 size={16} strokeWidth={1.8} /> Approve &amp; publish to Memory Lane</>}
                    </button>
                </section>
            )}
        </div>
    );
}

// ---------- Sub-components ----------

function Field({ label, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551]">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}

function FieldGroup({ children }) {
    return <div className="space-y-3">{children}</div>;
}

function BodyEditor({ article, onChange }) {
    const body = article.body || [];
    const setBlock = (i, patch) => {
        const next = body.slice();
        next[i] = { ...next[i], ...patch };
        onChange({ ...article, body: next });
    };
    const removeBlock = (i) => {
        const next = body.slice(); next.splice(i, 1);
        onChange({ ...article, body: next });
    };
    const addBlock = (type) => {
        const base = type === "ul" || type === "ol" ? { type, items: [""] } : type === "callout" ? { type, tone: "teal", text: "" } : { type, text: "" };
        onChange({ ...article, body: [...body, base] });
    };
    return (
        <div className="mt-5" data-testid="studio-body-editor">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] mb-2">Body</p>
            <ul className="space-y-2">
                {body.map((b, i) => (
                    <li key={i} className="rounded-xl border border-cream-dark bg-white p-3" data-testid={`studio-body-block-${i}`}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                            <select className="text-[11px] uppercase tracking-wider font-semibold text-teal-dark bg-transparent border-none cursor-pointer focus:outline-none" value={b.type} onChange={(e) => setBlock(i, { type: e.target.value })}>
                                <option value="p">Paragraph</option>
                                <option value="h2">Heading H2</option>
                                <option value="ul">Bullet list</option>
                                <option value="ol">Numbered list</option>
                                <option value="callout">Callout</option>
                            </select>
                            <button onClick={() => removeBlock(i)} aria-label="Remove block" className="text-[#6E7A74] hover:text-coral-dark" data-testid={`studio-body-remove-${i}`}>
                                <Minus size={14} strokeWidth={1.8} />
                            </button>
                        </div>
                        {(b.type === "ul" || b.type === "ol") ? (
                            <textarea rows={3} className="ml-input w-full font-mono text-[13px]" value={(b.items || []).join("\n")} onChange={(e) => setBlock(i, { items: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} />
                        ) : b.type === "callout" ? (
                            <>
                                <select className="ml-input w-32 mb-1" value={b.tone || "teal"} onChange={(e) => setBlock(i, { tone: e.target.value })}>
                                    <option value="teal">Tip (teal)</option>
                                    <option value="coral">Caution (coral)</option>
                                </select>
                                <textarea rows={2} className="ml-input w-full" value={b.text || ""} onChange={(e) => setBlock(i, { text: e.target.value })} />
                            </>
                        ) : (
                            <textarea rows={b.type === "h2" ? 1 : 3} className="ml-input w-full" value={b.text || ""} onChange={(e) => setBlock(i, { text: e.target.value })} />
                        )}
                    </li>
                ))}
            </ul>
            <div className="flex flex-wrap gap-2 mt-3">
                {["p", "h2", "ul", "ol", "callout"].map((t) => (
                    <button key={t} onClick={() => addBlock(t)} className="text-xs text-teal-dark hover:underline inline-flex items-center gap-1" data-testid={`studio-body-add-${t}`}>
                        <Plus size={12} strokeWidth={2} /> {t}
                    </button>
                ))}
            </div>
        </div>
    );
}

function FaqEditor({ article, onChange }) {
    const faq = article.faq || [];
    const set = (i, patch) => {
        const next = faq.slice(); next[i] = { ...next[i], ...patch }; onChange({ ...article, faq: next });
    };
    const remove = (i) => {
        const next = faq.slice(); next.splice(i, 1); onChange({ ...article, faq: next });
    };
    const add = () => onChange({ ...article, faq: [...faq, { q: "", a: "" }] });
    return (
        <div className="mt-5" data-testid="studio-faq-editor">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] mb-2">FAQ ({faq.length})</p>
            <ul className="space-y-2">
                {faq.map((f, i) => (
                    <li key={i} className="rounded-xl border border-cream-dark bg-white p-3" data-testid={`studio-faq-${i}`}>
                        <div className="flex justify-between items-start gap-2">
                            <input className="ml-input w-full font-semibold" placeholder="Question?" value={f.q || ""} onChange={(e) => set(i, { q: e.target.value })} />
                            <button onClick={() => remove(i)} aria-label="Remove" className="text-[#6E7A74] hover:text-coral-dark mt-2"><Minus size={14} strokeWidth={1.8} /></button>
                        </div>
                        <textarea rows={2} className="ml-input w-full mt-1.5" placeholder="Answer" value={f.a || ""} onChange={(e) => set(i, { a: e.target.value })} />
                    </li>
                ))}
            </ul>
            <button onClick={add} className="text-xs text-teal-dark hover:underline inline-flex items-center gap-1 mt-2" data-testid="studio-faq-add">
                <Plus size={12} strokeWidth={2} /> Add FAQ
            </button>
        </div>
    );
}

function RefEditor({ article, onChange }) {
    const refs = article.references || [];
    const set = (i, patch) => { const n = refs.slice(); n[i] = { ...n[i], ...patch }; onChange({ ...article, references: n }); };
    const remove = (i) => { const n = refs.slice(); n.splice(i, 1); onChange({ ...article, references: n }); };
    const add = () => onChange({ ...article, references: [...refs, { label: "", url: "" }] });
    return (
        <div className="mt-5" data-testid="studio-ref-editor">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] mb-2">References ({refs.length})</p>
            <ul className="space-y-2">
                {refs.map((r, i) => (
                    <li key={i} className="rounded-xl border border-cream-dark bg-white p-3 grid sm:grid-cols-[1fr_2fr_auto] gap-2 items-center" data-testid={`studio-ref-${i}`}>
                        <input className="ml-input w-full" placeholder="Source label" value={r.label || ""} onChange={(e) => set(i, { label: e.target.value })} />
                        <input className="ml-input w-full font-mono text-[12px]" placeholder="https://…" value={r.url || ""} onChange={(e) => set(i, { url: e.target.value })} />
                        <button onClick={() => remove(i)} aria-label="Remove" className="text-[#6E7A74] hover:text-coral-dark"><Minus size={14} strokeWidth={1.8} /></button>
                    </li>
                ))}
            </ul>
            <button onClick={add} className="text-xs text-teal-dark hover:underline inline-flex items-center gap-1 mt-2" data-testid="studio-ref-add">
                <Plus size={12} strokeWidth={2} /> Add reference
            </button>
        </div>
    );
}

const STATUS_TONE = {
    pass: { Icon: CheckCircle2, color: "text-stage_early-dark", bg: "bg-stage_early/15" },
    warn: { Icon: AlertTriangle, color: "text-yellow-dark", bg: "bg-yellow-soft" },
    fail: { Icon: XCircle, color: "text-coral-dark", bg: "bg-coral-light/60" },
};

function ValidationReport({ validation, onRerun, onApplyFixes, applyingFixes }) {
    const checks = validation.checks || [];
    const fixes = validation.suggested_fixes || [];
    const hasIssues = checks.some((c) => c.status !== "pass");
    return (
        <section className="ml-card p-5 sm:p-6" data-testid="studio-validation">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center"><ListChecks size={16} strokeWidth={1.8} /></span>
                    <div>
                        <h3 className="font-heading text-lg text-[#1F2624]">AI Review</h3>
                        <p className="text-xs text-[#6E7A74]">Clinical + SEO checks. Re-run after edits.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {hasIssues && onApplyFixes && (
                        <button
                            onClick={onApplyFixes}
                            disabled={applyingFixes}
                            data-testid="studio-validation-apply-fixes"
                            className="ml-btn-primary !min-h-[36px] !py-1.5 !px-3 text-xs"
                            title="Softens overly broad wording, removes outdated thresholds, inserts [CITATION NEEDED] markers, and adds [INTERNAL LINK] placeholders."
                        >
                            {applyingFixes ? <><Loader2 className="animate-spin" size={12} /> Applying…</> : <><Sparkles size={12} strokeWidth={2} /> Apply suggested fixes</>}
                        </button>
                    )}
                    <button onClick={onRerun} className="text-xs inline-flex items-center gap-1 text-teal-dark hover:underline" data-testid="studio-validation-rerun">
                        <RefreshCcw size={12} strokeWidth={1.8} /> Re-run
                    </button>
                </div>
            </div>
            <ul className="mt-4 space-y-2" data-testid="studio-validation-list">
                {checks.map((c, i) => {
                    const tone = STATUS_TONE[c.status] || STATUS_TONE.warn;
                    const Icon = tone.Icon;
                    return (
                        <li key={i} className={`rounded-xl p-3 flex items-start gap-2 ${tone.bg}`} data-testid={`studio-check-${c.id}`}>
                            <Icon size={16} strokeWidth={2} className={`${tone.color} shrink-0 mt-0.5`} />
                            <div className="flex-1">
                                <p className={`text-sm font-semibold ${tone.color}`}>{c.label}</p>
                                <p className="text-sm text-[#1F2624] leading-relaxed">{c.note}</p>
                            </div>
                        </li>
                    );
                })}
            </ul>
            {fixes.length > 0 && (
                <>
                    <h4 className="mt-4 mb-1 text-[10px] uppercase tracking-wider font-semibold text-[#4C5551]">Suggested fixes</h4>
                    <ul className="text-sm text-[#1F2624] list-disc list-inside space-y-1">
                        {fixes.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                    <p className="text-[11px] text-[#6E7A74] mt-2">
                        Click <strong>Apply suggested fixes</strong> above to have the AI soften broad wording,
                        strip outdated numeric thresholds, and insert <code>[CITATION NEEDED]</code> + <code>[INTERNAL LINK]</code> placeholders for you to fill in.
                    </p>
                </>
            )}
        </section>
    );
}

function ChangesReport({ changes, onDismiss }) {
    const items = changes?.items || [];
    const empty = items.length === 0;
    return (
        <section className="ml-card p-5 sm:p-6 border-2 border-stage_early/40 bg-stage_early/10" data-testid="studio-changes">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-2xl bg-stage_early/30 text-stage_early-dark flex items-center justify-center">
                        <FileEdit size={16} strokeWidth={1.8} />
                    </span>
                    <div>
                        <h3 className="font-heading text-lg text-[#1F2624]">
                            {empty ? "No material changes" : `Applied ${changes.count} change${changes.count === 1 ? "" : "s"}`}
                        </h3>
                        <p className="text-xs text-[#4C5551]">
                            {empty
                                ? "The AI didn't find anything worth rewriting. Edit manually or re-run AI Review."
                                : "Article body, references, and metadata were revised. Draft was auto-saved. Re-run AI Review to see the new score."}
                        </p>
                    </div>
                </div>
                <button
                    onClick={onDismiss}
                    aria-label="Dismiss changes summary"
                    className="text-xs text-[#6E7A74] hover:text-coral-dark"
                    data-testid="studio-changes-dismiss"
                >
                    Dismiss
                </button>
            </div>

            {!empty && (
                <>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3 text-xs">
                        <div className="rounded-xl bg-white border border-cream-dark p-3">
                            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551]">Before</div>
                            <div className="mt-1 text-[#1F2624]">
                                {changes.before_summary?.ref_count ?? 0} reference(s)
                                <span className="text-[#6E7A74]"> · </span>
                                {(changes.before_summary?.body_chars ?? 0).toLocaleString()} chars body
                            </div>
                        </div>
                        <div className="rounded-xl bg-white border border-stage_early/40 p-3">
                            <div className="text-[10px] uppercase tracking-wider font-semibold text-stage_early-dark">After</div>
                            <div className="mt-1 text-[#1F2624]">
                                {changes.after_summary?.ref_count ?? 0} reference(s)
                                <span className="text-[#6E7A74]"> · </span>
                                {(changes.after_summary?.body_chars ?? 0).toLocaleString()} chars body
                            </div>
                        </div>
                    </div>

                    <ul className="mt-4 space-y-2" data-testid="studio-changes-list">
                        {items.map((it, i) => (
                            <li key={i} className="rounded-xl bg-white border border-cream-dark p-3" data-testid={`studio-changes-item-${i}`}>
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="text-[10px] uppercase tracking-wider font-semibold text-stage_early-dark">{it.field}</span>
                                    <span className="text-sm text-[#1F2624]">{it.summary}</span>
                                </div>
                                {it.details && it.details.length > 0 && (
                                    <ul className="mt-1.5 text-xs text-[#4C5551] space-y-1">
                                        {it.details.map((d, k) => (
                                            <li key={k} className="font-mono whitespace-pre-wrap leading-snug break-words">
                                                {d}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </section>
    );
}

function RelatedReport({ related, article }) {
    if (!related || related.length === 0) {
        return (
            <section className="ml-card p-5 sm:p-6" data-testid="studio-related-empty">
                <div className="flex items-center gap-2 mb-2">
                    <span className="w-9 h-9 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center"><Link2 size={16} strokeWidth={1.8} /></span>
                    <h3 className="font-heading text-lg text-[#1F2624]">Related articles</h3>
                </div>
                <p className="text-sm text-[#4C5551]">No strong matches in the existing 94-article library. Consider adding tags or a more specific H1.</p>
            </section>
        );
    }
    return (
        <section className="ml-card p-5 sm:p-6" data-testid="studio-related">
            <div className="flex items-center gap-2 mb-2">
                <span className="w-9 h-9 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center"><Link2 size={16} strokeWidth={1.8} /></span>
                <div>
                    <h3 className="font-heading text-lg text-[#1F2624]">Internal links to add</h3>
                    <p className="text-xs text-[#6E7A74]">Memory Lane will auto-render these as the article&apos;s &quot;Related reading&quot; block.</p>
                </div>
            </div>
            <ul className="mt-2 space-y-1.5">
                {related.map((r) => (
                    <li key={`${r.group}/${r.slug}`} className="flex items-start gap-2 text-sm" data-testid={`studio-related-${r.slug}`}>
                        <BookOpen size={14} strokeWidth={1.8} className="text-teal-dark mt-0.5 shrink-0" />
                        <a href={`/${r.group}/${r.slug}`} target="_blank" rel="noopener noreferrer" className="text-[#1F2624] hover:text-teal-dark underline decoration-cream-dark">
                            {r.h1}
                        </a>
                        <span className="text-[10px] uppercase tracking-wider text-[#6E7A74] ml-1">{r.group}</span>
                        <ExternalLink size={11} strokeWidth={1.8} className="text-[#6E7A74] mt-0.5" />
                    </li>
                ))}
            </ul>
            <p className="text-xs text-[#6E7A74] mt-3">
                These will appear in this article&apos;s &quot;Related reading&quot; section once published, and your new article will reciprocally appear in theirs (via tag overlap).
            </p>
        </section>
    );
}
